import { Suspense, useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { AppVersionChecker } from 'shared/app/AppVersionChecker';
import { useAuthState } from 'shared/auth/AuthState';
import { getAuthRole, saveCarrierGuid } from 'shared/auth/AuthToken';
import { LogoutContainerPage } from 'shared/auth/LogoutContainerPage';
import { useShowSubscriptionCancelledDialog } from 'shared/errors/data/useShowSubscriptionCancelledDialog';
import { ServerErrorBoundary } from 'shared/errors/ServerErrorBoundary';
import { identifyAnalytics } from 'shared/helpers/Analytics';
import { identifyUser } from 'shared/helpers/Logger';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import { NavigationHeaderBanner } from 'shared/layout/NavigationHeaderBanner';
import { NavigationHeaderBannerContainer } from 'shared/layout/NavigationHeaderBannerContainer';
import { NotificationStack } from 'shared/layout/notification-stack/NotificationStack';
import { SubscriptionPaymentBanner } from 'shared/layout/notification-stack/core/SubscriptionPaymentBanner';
import { NavigationBarContainer } from 'shared/layout/root/NavigationBarContainer';
import { AppLayoutSidebarLegacy } from 'shared/layout/sidebar/AppLayoutSidebarLegacy';
import { NavbarBottomBar } from 'shared/layout/sidebar/NavbarBottomBar';
import { NavbarContextProvider } from 'shared/layout/sidebar/NavbarContext';
import { useBottomItems } from 'shared/layout/sidebar/useBottomItems';
import { useLoadboardNudgeIndicator } from 'shared/layout/sidebar/useLoadboardNudgeIndicator';
import { SplashScreen } from 'shared/layout/SplashScreen';
import { useDispatcherProfile } from 'shared/modules/dispatcher/DispatcherAPI';
import { LoadboardAccessContext } from 'shared/modules/loadboard/core/loadboard-access/LoadboardAccessContext';
import { LoadboardInsuranceExpiringDialog } from 'shared/modules/loadboard/core/loadboard-access/LoadboardInsuranceExpiringDialog';
import { useLoadboardNotifications } from 'shared/modules/loadboard/LoadboardNotifications';
import { OnboardingWrapper } from 'shared/modules/onboarding/core/OnboardingWrapper';
import { OnboardingDialogs } from 'shared/modules/onboarding/OnboardingDialogs';
import { useOnboardingTourHandler } from 'shared/modules/onboarding/OnboardingIntercomTours';
import { PermissionView } from 'shared/modules/permission/core/PermissionView';
import { useUserPermissions } from 'shared/modules/permission/PermissionAPI';
import { useRatingNotifications } from 'shared/modules/ratings/data/RatingNotifications';
import { useShowAddBillingAddressDialog } from 'shared/modules/subscription/core/useShowAddBillingAddressDialog';
import { SubscriptionAddBillingAddressDialog } from 'shared/modules/subscription/SubscriptionAddBillingAddressDialog';
import { RedirectToLogin } from 'shared/routing/RedirectToLogin';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { useBillingAddress } from 'shared/modules/subscription/SubscriptionAPI';
import { getEnabledFeaturesForIntercom } from 'shared/helpers/Intercom';
import { basePath } from 'shared/utils/RouterUtils';
import { trackLoadboardEvent } from './core/LoadboardAnalytics';
import { PostingsPageContextProvider } from './core/postings/PostingsPageContext';
import { TrialExpiredDialog } from './core/TrialExpiredDialog';
import { LoadboardContextProvider } from './data/LoadboardContext';
import { loadboardMobileRoutes } from './LoadboardMobileRoutes';
import { lazyImport } from 'shared/routing/LazyRoute';

const TermsAndConditionsPage = lazyImport(
  () => import('./LoadboardPages'),
  'TermsAndConditionsPage',
);
const PostingNotificationsSettingsPage = lazyImport(
  () => import('./LoadboardPages'),
  'PostingNotificationsSettingsPage',
);
const PostingListPage = lazyImport(
  () => import('./LoadboardPages'),
  'PostingListPage',
);
const ShipperProfilePage = lazyImport(
  () => import('./LoadboardPages'),
  'ShipperProfilePage',
);

const localPaths = { loadboard: '/' };

function LoadboardAppRoot() {
  const authState = useAuthState();
  const { data: settings } = useCarrierSettings();
  const { markIndicatorAsSeen } = useLoadboardNudgeIndicator();

  const { isOpenUpgradeWarningDialog, closeUpgradeWarningDialog } =
    useShowAddBillingAddressDialog();

  const {
    isOpenSubscriptionCancelledDialog,
    closeSubscriptionCancelledDialog,
    trialType,
  } = useShowSubscriptionCancelledDialog();
  const role = getAuthRole();

  const { bottomItems, hasMenuBadge } = useBottomItems();

  useEffect(() => {
    markIndicatorAsSeen();
  }, [markIndicatorAsSeen]);

  useLoadboardNotifications();
  useRatingNotifications();
  useBillingAddress();
  useDispatcherProfile();
  useUserPermissions();

  useOnboardingTourHandler({
    tourName: 'tour_verification_approval',
    enabled: settings?.carrier.verification_status === 'approved',
  });

  useEffect(() => {
    if (settings?.carrier.guid) {
      saveCarrierGuid(settings.carrier.guid);
    }
    if (settings) {
      identifyUser({
        id: settings.user.guid,
        carrier_guid: settings.carrier.guid,
        carrier_name: settings.carrier.name,
      });
      identifyAnalytics({
        guid: settings.user.guid,
        hash: settings.user.user_hash,
        experiment_group: settings.groups?.map((e) => e.number),
        accountType: settings.company_type,
        enabled_features: getEnabledFeaturesForIntercom(settings.features, [
          'supercard_management',
        ]),
        company: {
          id: settings.carrier.guid,
          guid: settings.carrier.guid,
          name: settings.carrier.name,
          is_super: settings.carrier.verification_status === 'approved',
          superpay_onboarding_status: settings.superpay.verification,
          is_fmcsa_phone_verified: settings.is_fmcsa_phone_verified,
        },
      });

      trackLoadboardEvent({ name: 'SLB: Opened Loadboard' });
    }
  }, [settings]);

  if (authState === 'undetermined') {
    return <SplashScreen />;
  }

  if (authState === 'unauthorized') {
    return <RedirectToLogin />;
  }

  return (
    <ServerErrorBoundary subscribe={['Unauthorized', 'AUTH_ERROR']}>
      <OnboardingWrapper>
        <NavbarContextProvider>
          <LoadboardContextProvider>
            {role === 'admin' && (
              <NavigationHeaderBannerContainer>
                <NavigationHeaderBanner />
              </NavigationHeaderBannerContainer>
            )}
            <SubscriptionAddBillingAddressDialog
              open={Boolean(isOpenUpgradeWarningDialog)}
              onClose={closeUpgradeWarningDialog}
            />

            <OnboardingDialogs />

            <TrialExpiredDialog
              open={Boolean(isOpenSubscriptionCancelledDialog)}
              onClose={() => {
                closeSubscriptionCancelledDialog();
              }}
              onReactivate={() => {
                closeSubscriptionCancelledDialog();
                openExternalURL('/tms/settings/subscription');
              }}
              type={trialType === 'expired' ? 'trial_expired' : 'cancelled'}
            />

            <LoadboardInsuranceExpiringDialog />

            <NavigationBarContainer
              banner={<SubscriptionPaymentBanner isLoadboard={true} />}
              navigationBar={
                <AppLayoutSidebarLegacy
                  hasMenuBadge={hasMenuBadge}
                  localPaths={localPaths}
                />
              }
              mobileMenuBar={
                <NavbarBottomBar
                  items={bottomItems}
                  hasMenuBadge={hasMenuBadge}
                  localPaths={localPaths}
                />
              }
            >
              <NotificationStack isLoadboard={true} />

              <LoadboardAccessContext>
                <Suspense fallback={<SplashScreen />}>
                  <PermissionView name="marketplace" permission="can_view">
                    <Outlet />
                  </PermissionView>
                </Suspense>
              </LoadboardAccessContext>
            </NavigationBarContainer>
          </LoadboardContextProvider>
        </NavbarContextProvider>
      </OnboardingWrapper>
    </ServerErrorBoundary>
  );
}

export const router = createBrowserRouter(
  [
    {
      element: <LoadboardAppRoot />,
      children: [
        {
          path: 'shipper/:guid',
          element: <ShipperProfilePage />,
        },
        {
          path: 'terms-and-conditions/:guid',
          element: <TermsAndConditionsPage />,
        },
        {
          path: 'loads',
          element: (
            <PostingsPageContextProvider>
              <PostingListPage />
              <AppVersionChecker />
            </PostingsPageContextProvider>
          ),
        },
        {
          path: 'settings/notifications',
          element: (
            <PermissionView
              name="loadboard_notifications"
              permission="can_view"
            >
              <PostingNotificationsSettingsPage />
            </PermissionView>
          ),
        },
        { path: 'logout', element: <LogoutContainerPage /> },
        { path: 'dispatchers/*', element: <Navigate to="/users" /> },
        { path: '*', element: <Navigate to="/loads" /> },
      ],
    },
  ],
  { basename: basePath },
);

export const loadboardMobileRouter = createBrowserRouter(
  [{ element: <LoadboardAppRoot />, children: loadboardMobileRoutes }],
  { basename: basePath },
);
